import * as RadixPopover from '@radix-ui/react-popover';
import React, { useState, useCallback } from 'react';
import { cn } from '@restworld/utils-common';

export enum PopoverTrigger {
  CLICK = 'click',
  HOVER = 'hover',
}

/** Backward-compatible alias for PopoverTrigger */
export const Trigger = PopoverTrigger;

export interface PopoverProps {
  /** Should contain Popover.Button and Popover.Panel */
  children: React.ReactNode;
  /** How the popover is opened. Defaults to CLICK. */
  trigger?: PopoverTrigger;
  /** Extra classes on the root wrapper */
  className?: string;
  /** Extra classes on the trigger button */
  buttonClassName?: string;
  /** Extra classes on the content panel */
  panelClassName?: string;
  /** Whether the popover stretches to full width */
  fullWidth?: boolean;
  /**
   * Whether to apply opinionated default button styles.
   * Defaults to true for backward compatibility.
   */
  haveDefaultButtonStyle?: boolean;
}

/**
 * A customizable popover component that can be triggered by click or hover.
 *
 * Uses the compound-component pattern: nest `Popover.Button` and `Popover.Panel`
 * inside `Popover` to define the trigger element and dropdown content.
 *
 * @example
 * ```tsx
 * <Popover trigger={PopoverTrigger.HOVER}>
 *   <Popover.Button>Hover me</Popover.Button>
 *   <Popover.Panel>
 *     <p className="p-4">Hello, world!</p>
 *   </Popover.Panel>
 * </Popover>
 * ```
 */
export const Popover = ({
  children,
  trigger = PopoverTrigger.CLICK,
  className,
  buttonClassName,
  panelClassName,
  fullWidth = false,
  haveDefaultButtonStyle = true,
}: PopoverProps) => {
  const [open, setOpen] = useState(false);

  let buttonSlot: React.ReactNode | undefined;
  let panelSlot: React.ReactNode | undefined;

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      if (child.type === Popover.Button) {
        buttonSlot = child;
      } else if (child.type === Popover.Panel) {
        panelSlot = child;
      }
    }
  });

  const handleMouseEnter = useCallback(() => {
    if (trigger === PopoverTrigger.HOVER) {
      setOpen(true);
    }
  }, [trigger]);

  const handleMouseLeave = useCallback(() => {
    if (trigger === PopoverTrigger.HOVER) {
      setOpen(false);
    }
  }, [trigger]);

  const defaultButtonStyles =
    'relative p-2 text-black bg-gray-50 text-base cursor-pointer rounded-md hover:bg-gray-100 hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-primary-100 focus:ring-white md:bg-transparent md:truncate md:w-1/2 md:text-sm lg:text-base lg:w-fit';

  return (
    <RadixPopover.Root open={open} onOpenChange={setOpen}>
      <div
        className={cn('w-fit', fullWidth && 'w-full', className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <RadixPopover.Trigger asChild>
          <button
            type="button"
            className={cn(
              haveDefaultButtonStyle && defaultButtonStyles,
              !haveDefaultButtonStyle &&
                'focus-visible:border-none focus-visible:ring-0 focus-visible:shadow-none focus-visible:outline-none',
              fullWidth && 'w-full',
              buttonClassName
            )}
          >
            {buttonSlot}
          </button>
        </RadixPopover.Trigger>

        <RadixPopover.Portal>
          <RadixPopover.Content
            side="bottom"
            align="start"
            sideOffset={6}
            className={cn(
              '!flex w-fit p-6 bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-[900]',
              'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
              panelClassName
            )}
          >
            {panelSlot}
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </div>
    </RadixPopover.Root>
  );
};

/** Slot component for the trigger element */
Popover.Button = ({ children }: { children: React.ReactNode }) => {
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

/** Slot component for the panel content */
Popover.Panel = ({ children }: { children: React.ReactNode }) => {
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};
